import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Megaphone, Heart, Users, Database, Gift, ArrowRight, Target, Wrench, Handshake } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { programs } from '../data/mock';

const ProgramsPage = () => {
  const iconMap = {
    BookOpen: BookOpen,
    Megaphone: Megaphone,
    Heart: Heart,
    Users: Users,
    Database: Database,
    Gift: Gift
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="bg-[#5D4E6D] py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#E07A5F]/20 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">Our Programs</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6 leading-tight">
              Comprehensive Cancer Support
            </h1>
            <p className="text-gray-200 text-lg leading-relaxed">
              We offer a range of programs designed to educate, support, and empower cancer patients, 
              survivors, and their caregivers throughout Hawaii.
            </p>
          </div>
        </div>
      </section>

      {/* Programs Detail */}
      <section className="py-24 bg-[#F5F0EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">Our Framework</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2D2A32] mt-4 mb-4">
              Five Pillars of Impact
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Each program is anchored in three questions: who we serve, how we deliver, and who we partner with.
            </p>
          </div>

          <div className="space-y-10">
            {programs.map((program, index) => {
              const IconComponent = iconMap[program.icon] || Heart;
              return (
                <Card
                  key={program.id}
                  data-testid={`program-card-${program.id}`}
                  className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden"
                >
                  <div className="h-2 bg-gradient-to-r from-[#5D4E6D] to-[#E07A5F]" />
                  <div className="grid lg:grid-cols-3 gap-0">
                    {/* Left: Title + Description */}
                    <div className="lg:col-span-1 p-8 lg:p-10 bg-[#5D4E6D] text-white flex flex-col justify-center">
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur">
                          <IconComponent className="w-7 h-7 text-[#E07A5F]" />
                        </div>
                        <span className="text-[#E07A5F] font-bold text-sm uppercase tracking-wider">
                          0{index + 1}
                        </span>
                      </div>
                      <h3 className="text-3xl font-bold mb-4 leading-tight">{program.title}</h3>
                      <p className="text-gray-200 leading-relaxed">{program.description}</p>
                    </div>

                    {/* Right: Three columns of detail */}
                    <CardContent className="lg:col-span-2 p-8 lg:p-10 grid sm:grid-cols-3 gap-6">
                      <div>
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-8 h-8 bg-[#E07A5F]/10 rounded-lg flex items-center justify-center">
                            <Target className="w-4 h-4 text-[#E07A5F]" />
                          </div>
                          <h4 className="font-bold text-[#2D2A32] text-sm uppercase tracking-wider">
                            Who We Serve
                          </h4>
                        </div>
                        <ul className="space-y-2">
                          {program.audiences.map((audience) => (
                            <li key={audience} className="text-gray-600 text-sm flex items-start">
                              <span className="w-1.5 h-1.5 bg-[#E07A5F] rounded-full mt-2 mr-2 flex-shrink-0" />
                              {audience}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-8 h-8 bg-[#5D4E6D]/10 rounded-lg flex items-center justify-center">
                            <Wrench className="w-4 h-4 text-[#5D4E6D]" />
                          </div>
                          <h4 className="font-bold text-[#2D2A32] text-sm uppercase tracking-wider">
                            How We Deliver
                          </h4>
                        </div>
                        <ul className="space-y-2">
                          {program.methods.map((method) => (
                            <li key={method} className="text-gray-600 text-sm flex items-start">
                              <span className="w-1.5 h-1.5 bg-[#5D4E6D] rounded-full mt-2 mr-2 flex-shrink-0" />
                              {method}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-8 h-8 bg-[#E07A5F]/10 rounded-lg flex items-center justify-center">
                            <Handshake className="w-4 h-4 text-[#E07A5F]" />
                          </div>
                          <h4 className="font-bold text-[#2D2A32] text-sm uppercase tracking-wider">
                            Key Partners
                          </h4>
                        </div>
                        <ul className="space-y-2">
                          {program.partners.map((partner) => (
                            <li key={partner} className="text-gray-600 text-sm flex items-start">
                              <span className="w-1.5 h-1.5 bg-[#E07A5F] rounded-full mt-2 mr-2 flex-shrink-0" />
                              {partner}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How We Help */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">How We Help</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2D2A32] mt-4">
              Our Support Journey
            </h2>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-1 bg-[#5D4E6D]/20 transform -translate-x-1/2" />
            
            <div className="space-y-12">
              {[
                {
                  step: 1,
                  title: 'Initial Contact',
                  description: 'Reach out to us through our contact form, phone, or by attending one of our events.'
                },
                {
                  step: 2,
                  title: 'Needs Assessment',
                  description: 'We learn about your situation and identify how we can best support you and your family.'
                },
                {
                  step: 3,
                  title: 'Resource Connection',
                  description: 'We connect you with relevant programs, healthcare providers, and community resources.'
                },
                {
                  step: 4,
                  title: 'Ongoing Support',
                  description: 'We continue to support you throughout your journey with events, resources, and community.'
                }
              ].map((item, index) => (
                <div key={item.step} className={`flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                    <Card className="border-0 shadow-lg inline-block">
                      <CardContent className="p-6">
                        <h3 className="text-xl font-bold text-[#2D2A32] mb-2">{item.title}</h3>
                        <p className="text-gray-600">{item.description}</p>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="hidden md:flex w-16 h-16 bg-[#E07A5F] rounded-full items-center justify-center text-white text-2xl font-bold z-10 flex-shrink-0">
                    {item.step}
                  </div>
                  <div className="flex-1 hidden md:block" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#2D2A32]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Get Support?
          </h2>
          <p className="text-gray-300 text-lg mb-10">
            Contact us today to learn more about our programs and how we can help you or your loved ones.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-[#E07A5F] hover:bg-[#C96A50] text-white rounded-full px-10 transition-all duration-300"
          >
            <Link to="/contact">
              Contact Us
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default ProgramsPage;
